/**
 * Analytics Page - Revenue and user charts
 */

import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  AreaChart,
  Area,
} from 'recharts';
import { apiService } from '../services/apiService';
import './AnalyticsPage.css';

const AnalyticsPage: React.FC = () => {
  const [period, setPeriod] = useState<'7d' | '30d'>('30d');
  const [revenueData, setRevenueData] = useState<Array<{ date: string; revenue: number; volume: number; betCount: number }>>([]);
  const [userData, setUserData] = useState<Array<{ date: string; newUsers: number; totalUsers: number }>>([]);
  const [referralSummary, setReferralSummary] = useState<{ clicks: number; attributions: number; conversions: number; payoutCents: number } | null>(null);
  const [campaignPerformance, setCampaignPerformance] = useState<Array<{ id: string; key: string; clicks: number; attributions: number; payoutCents: number }>>([]);
  const [abuseReview, setAbuseReview] = useState<Array<{ id: string; referrerEmail: string; referredEmail: string; suspicionReason: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [revRes, userRes] = await Promise.all([
          apiService.getRevenueAnalytics(period as '7d' | '30d'),
          apiService.getUserAnalytics(period as '7d' | '30d'),
        ]);
        const [referralRes, abuseRes] = await Promise.all([
          apiService.getReferralAnalytics(),
          apiService.getReferralAbuseReview(),
        ]);
        setRevenueData(revRes.data || []);
        setUserData(userRes.data || []);
        setReferralSummary(referralRes.summary);
        setCampaignPerformance(referralRes.campaigns || []);
        setAbuseReview(abuseRes.events || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [period]);

  if (loading) return <div className="analytics-page"><div className="loading">Loading analytics...</div></div>;
  if (error) return <div className="analytics-page"><div className="error">{error}</div></div>;

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <h1>Analytics</h1>
        <select value={period} onChange={(e) => setPeriod(e.target.value as '7d' | '30d')}>
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
        </select>
      </div>
      <div className="analytics-charts">
        <div className="chart-card">
          <h2>Revenue & Volume</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip formatter={(v) => (v != null ? `£${Number(v).toFixed(2)}` : '')} />
              <Legend />
              <Line yAxisId="left" type="monotone" dataKey="revenue" stroke="#10b981" name="Revenue" />
              <Line yAxisId="right" type="monotone" dataKey="volume" stroke="#3b82f6" name="Volume" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="chart-card">
          <h2>User Growth</h2>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={userData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="newUsers" stroke="#8b5cf6" fill="#8b5cf6" name="New Users" />
              <Line type="monotone" dataKey="totalUsers" stroke="#f59e0b" name="Total Users" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      {referralSummary && (
        <div className="analytics-charts" style={{ marginTop: 24 }}>
          <div className="chart-card">
            <h2>Share &amp; Earn Funnel</h2>
            <p>Clicks: {referralSummary.clicks}</p>
            <p>Attributions: {referralSummary.attributions}</p>
            <p>Conversions: {referralSummary.conversions}</p>
            <p>Payouts: £{(referralSummary.payoutCents / 100).toFixed(2)}</p>
          </div>
          <div className="chart-card">
            <h2>Suspicious Attributions</h2>
            {abuseReview.length === 0 ? (
              <p>No suspicious attributions currently flagged.</p>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                {abuseReview.slice(0, 5).map((event) => (
                  <div key={event.id}>
                    <strong>{event.referrerEmail}</strong> → {event.referredEmail}
                    <div>{event.suspicionReason}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      <div className="chart-card" style={{ marginTop: 24 }}>
        <h2>Campaign Performance</h2>
        {campaignPerformance.length === 0 ? (
          <p>No referral campaigns yet.</p>
        ) : (
          <table style={{ width: '100%' }}>
            <thead>
              <tr>
                <th align="left">Campaign</th>
                <th align="left">Clicks</th>
                <th align="left">Attributions</th>
                <th align="left">Payouts</th>
              </tr>
            </thead>
            <tbody>
              {campaignPerformance.map((campaign) => (
                <tr key={campaign.id}>
                  <td>{campaign.key}</td>
                  <td>{campaign.clicks}</td>
                  <td>{campaign.attributions}</td>
                  <td>£{(campaign.payoutCents / 100).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AnalyticsPage;
